import CustomButton from "components/CustomButton/CustomButton";
import OpenEyeIcon from "icons/OpenEyeIcon";
import ReasignationIcon from "icons/ReasignationIcon";
import CheckIcon from "icons/CheckIcon";
import ErrorIcon from "icons/ErrorIcon";
import StopIcon from "icons/StopIcon";
import TimerIcon from "icons/TimerIcon";
import ActivityIcon from "icons/ActivityIcon";
import FileIcon2 from "icons/FileIcon2";
import ArrowIcon from "icons/ArrowIcon";
import {
	ActionItem,
	ActionItemLabel,
	ActionsRow,
	CellLabel,
	DashboardCard,
	EmptyState,
	FilterBar,
	FilterButton,
	FilterCount,
	LegendGroup,
	LegendItems,
	LegendLabel,
	LegendRail,
	PaginationActions,
	PaginationArrow,
	PaginationButton,
	PaginationInfo,
	PaginationPageChip,
	PaginationRow,
	PrimaryValue,
	ResultsSummary,
	SearchCluster,
	SearchInput,
	SignalBadge,
	SignalLegendItem,
	SignalStack,
	SectionHeaderRow,
	SectionTitle,
	SectionTitleGroup,
	SecondaryValue,
	StudentIdentity,
	TableCell,
	TableHeader,
	TableRow,
	TableShell,
	ToolbarCluster,
} from "../styles";

const StudentsSection = ({
	search,
	onSearchChange,
	onSearchSubmit,
	isLoading,
	isBusy,
	pdfLoadingStudentId,
	filters,
	statusFilter,
	onFilterChange,
	getFilterCount,
	summaryCounters,
	students,
	currentPage,
	pageSize,
	total,
	totalPages,
	onPageChange,
	isPaginationDisabled,
	hasPendingReprocess,
	formatDate,
	getStatusText,
	getStatusVariant,
	getEligibilityText,
	getEligibilityVariant,
	getErrorText,
	onViewPdf,
	onStudentReprocess,
}) => {
	const renderCertificateStatusIcon = status => {
		switch (status) {
			case "ISSUED":
				return <FileIcon2 color="#0f766e" size="16" />;
			case "FAILED":
			case "COMPLETED_WITH_ERRORS":
				return <ErrorIcon color="#b91c1c" />;
			case "PROCESSING":
				return <ActivityIcon />;
			case "PENDING_INITIAL":
			case "PENDING_REPROCESS":
			case "PENDING_PDF":
			default:
				return <TimerIcon size={18} color="#a16207" />;
		}
	};

	const renderEligibilityIcon = status => {
		if (status === "ELIGIBLE") {
			return <CheckIcon color="#0f766e" />;
		}
		return <StopIcon />;
	};

	const getRowTone = student => {
		if (student.certificateStatus === "FAILED") return "critical";
		if (
			student.certificateStatus === "PENDING_INITIAL" ||
			student.certificateStatus === "PENDING_PDF" ||
			student.certificateStatus === "PENDING_REPROCESS" ||
			student.certificateStatus === "PROCESSING"
		)
			return "warning";
		if (student.certificateStatus === "ISSUED") return "healthy";
		return "default";
	};

	const isStudentReprocessPending = student =>
		[
			"PENDING_INITIAL",
			"PENDING_PDF",
			"PENDING_REPROCESS",
			"PROCESSING",
		].includes(student.certificateStatus);

	const canRunStudentReprocess = student =>
		Boolean(student.canReprocess) &&
		!hasPendingReprocess &&
		!isStudentReprocessPending(student);
	const startRecord =
		total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
	const endRecord =
		total === 0 ? 0 : Math.min(currentPage * pageSize, total);
	const searchText = (search || "").trim();
	const summaryText = isLoading
		? "Cargando participantes..."
		: statusFilter || searchText
			? "Resultados filtrados"
			: "";

	return (
		<DashboardCard gap="16px">
			<SectionHeaderRow>
				<SectionTitleGroup>
					<SectionTitle>Participantes y constancias</SectionTitle>
				</SectionTitleGroup>

				<ToolbarCluster>
					<SearchCluster>
						<SearchInput
							value={search}
							onChange={onSearchChange}
							onBlur={onSearchSubmit}
							disabled={isBusy}
							placeholder="Buscar por código o nombre"
						/>
						<CustomButton
							variant="quintinary"
							size="small"
							text="Buscar"
							width="110px"
							disabled={isBusy}
							onClick={onSearchSubmit}
						/>
					</SearchCluster>
				</ToolbarCluster>
			</SectionHeaderRow>

			<FilterBar>
				{filters.map(filter => (
					<FilterButton
						key={filter.code || "all"}
						type="button"
						$active={statusFilter === filter.code}
						disabled={isBusy}
						onClick={() => onFilterChange(filter.code)}
					>
						{filter.label}
						<FilterCount>{getFilterCount(filter.code, summaryCounters)}</FilterCount>
					</FilterButton>
				))}
			</FilterBar>

			{summaryText ? (
				<ResultsSummary>{summaryText}</ResultsSummary>
			) : null}

			<LegendRail>
				<LegendGroup>
					<LegendLabel>Elegibilidad</LegendLabel>
					<LegendItems>
						<SignalLegendItem>
							<SignalBadge $variant="success" $size="legend">
								<CheckIcon color="#0f766e" />
							</SignalBadge>
							Elegible
						</SignalLegendItem>
						<SignalLegendItem>
							<SignalBadge $variant="neutral" $size="legend">
								<StopIcon />
							</SignalBadge>
							No elegible
						</SignalLegendItem>
					</LegendItems>
				</LegendGroup>
				<LegendGroup>
					<LegendLabel>Estado </LegendLabel>
					<LegendItems>
						<SignalLegendItem>
							<SignalBadge $variant="warning" $size="legend">
								<TimerIcon size={16} color="#a16207" />
							</SignalBadge>
							Pendiente
						</SignalLegendItem>
						<SignalLegendItem>
							<SignalBadge $variant="success" $size="legend">
								<FileIcon2 color="#0f766e" size="16" />
							</SignalBadge>
							Emitido
						</SignalLegendItem>
						<SignalLegendItem>
							<SignalBadge $variant="danger" $size="legend">
								<ErrorIcon color="#b91c1c" />
							</SignalBadge>
							Error
						</SignalLegendItem>
					</LegendItems>
				</LegendGroup>
				<LegendGroup>
					<LegendLabel>Acciones</LegendLabel>
					<LegendItems>
						<SignalLegendItem>
							<SignalBadge $variant="neutral" $size="legend">
								<OpenEyeIcon />
							</SignalBadge>
							Ver PDF
						</SignalLegendItem>
						<SignalLegendItem>
							<SignalBadge $variant="neutral" $size="legend">
								<ReasignationIcon size={16} />
							</SignalBadge>
							Reproceso Individual
						</SignalLegendItem>
					</LegendItems>
				</LegendGroup>
			</LegendRail>

			<TableShell>
				<TableHeader>
					<span>Participante</span>
					<span>Estado / elegibilidad</span>
					<span>Motivo / detalle</span>
					<span>Fecha</span>
					<span>Acciones</span>
				</TableHeader>

				{students.length === 0 ? (
					<EmptyState>
						No hay participantes para los filtros seleccionados.
					</EmptyState>
				) : (
					students.map(student => {
						const detailText = student.lastErrorCode
							? getErrorText(student.lastErrorCode, student.lastErrorMessage)
							: student.lastErrorMessage || "-";
						const relevantDate =
							student.issuedAt || student.lastProcessedAt || null;
						const relevantDateLabel = student.issuedAt
							? "Emitido"
							: student.lastProcessedAt
								? "Procesado"
								: "Fecha";

						return (
							<TableRow key={student.studentId} $tone={getRowTone(student)}>
								<TableCell>
									<CellLabel>Participante</CellLabel>
									<StudentIdentity>
										<PrimaryValue title={student.fullName || "-"}>
											{student.fullName || "-"}
										</PrimaryValue>
										<SecondaryValue>{student.studentId || "-"}</SecondaryValue>
									</StudentIdentity>
								</TableCell>

								<TableCell>
									<CellLabel> Elegibilidad / Estado</CellLabel>
									<SignalStack>
										<SignalBadge
											$variant={getEligibilityVariant(
												student.eligibilityStatus
											)}
											title={`Elegibilidad: ${getEligibilityText(
												student.eligibilityStatus
											)}`}
										>
											{renderEligibilityIcon(student.eligibilityStatus)}
										</SignalBadge>
										<SignalBadge
											$variant={getStatusVariant(student.certificateStatus)}
											title={`Estado: ${getStatusText(student.certificateStatus)}`}
										>
											{renderCertificateStatusIcon(student.certificateStatus)}
										</SignalBadge>
									</SignalStack>
								</TableCell>

								<TableCell>
									<CellLabel>Motivo / detalle</CellLabel>
									<SecondaryValue title={detailText}>{detailText}</SecondaryValue>
								</TableCell>

								<TableCell>
									<CellLabel>{relevantDateLabel}</CellLabel>
									<PrimaryValue>{formatDate(relevantDate)}</PrimaryValue>
								</TableCell>

								<TableCell>
									<CellLabel>Acciones</CellLabel>
									<ActionsRow>
										<ActionItem>
											<CustomButton
												variant="quintinary"
												size="very-small"
												icon={<OpenEyeIcon />}
												width="36px"
												height="36px"
												title="Ver PDF"
												ariaLabel={`Ver PDF de ${student.fullName || student.studentId}`}
												disabled={
													!student.canViewPdf ||
													isBusy ||
													pdfLoadingStudentId === student.studentId
												}
												onClick={() => onViewPdf(student.studentId)}
											/>
											<ActionItemLabel>Ver PDF</ActionItemLabel>
										</ActionItem>
										<ActionItem>
											<CustomButton
												variant="quintinary"
												size="very-small"
												icon={<ReasignationIcon size={16} />}
												width="36px"
												height="36px"
												title="Reprocesar"
												ariaLabel={`Reprocesar constancia de ${student.fullName || student.studentId}`}
												disabled={isBusy || !canRunStudentReprocess(student)}
												onClick={() => onStudentReprocess(student.studentId)}
											/>
											<ActionItemLabel>Reproceso</ActionItemLabel>
										</ActionItem>
									</ActionsRow>
								</TableCell>
							</TableRow>
						);
					})
				)}
			</TableShell>

			{total > pageSize ? (
				<PaginationRow>
					<PaginationInfo>
						<PaginationPageChip>
							Mostrando {startRecord}-{endRecord} de {total} alumnos
						</PaginationPageChip>
					</PaginationInfo>
					<PaginationActions>
						<PaginationButton
							type="button"
							disabled={isPaginationDisabled || currentPage <= 1}
							aria-label="Página anterior"
							title="Página anterior"
							onClick={() => onPageChange(currentPage - 1)}
						>
							<PaginationArrow $direction="left">
								<ArrowIcon
									color="currentColor"
									size={{ width: 10, height: 10 }}
								/>
							</PaginationArrow>
						</PaginationButton>
						<PaginationButton
							type="button"
							disabled={isPaginationDisabled || currentPage >= totalPages}
							aria-label="Página siguiente"
							title="Página siguiente"
							onClick={() => onPageChange(currentPage + 1)}
						>
							<PaginationArrow $direction="right">
								<ArrowIcon
									color="currentColor"
									size={{ width: 10, height: 10 }}
								/>
							</PaginationArrow>
						</PaginationButton>
					</PaginationActions>
				</PaginationRow>
			) : null}
		</DashboardCard>
	);
};

export default StudentsSection;
